import React, { useState } from 'react';
import { Search, Package, Boxes } from 'lucide-react';
import EmptyState from '../EmptyState';
import type { Product } from '../../types/product';

interface ProductListProps {
  products: Product[];
  onSelect: (product: Product | null) => void;
}

const ProductList: React.FC<ProductListProps> = ({ products, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.internalCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (products.length === 0) {
    return (
      <EmptyState
        title="Nenhum produto cadastrado"
        description="Cadastre seus produtos para começar a gerenciar seu estoque e vendas."
        action={
          <button
            onClick={() => onSelect(null)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Package className="h-5 w-5 mr-2" />
            Cadastrar Primeiro Produto
          </button>
        }
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-100">
      <div className="p-4 border-b">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="divide-y">
        {filteredProducts.map((product) => (
          <div
            key={product.id}
            onClick={() => onSelect(product)}
            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
                <p className="text-sm text-gray-500">Código: {product.internalCode}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">
                  R$ {product.sellingPrice.toFixed(2)}
                </p>
                <p className={`text-sm ${
                  product.currentStock <= product.minStock 
                    ? 'text-red-600' 
                    : 'text-green-600'
                }`}>
                  Estoque: {product.currentStock}
                </p>
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {product.category}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {product.subcategory}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductList;